import { useEffect, useState } from "react";
import { InventoryMetadata } from "./InventoryMetadata";
import { formatItemUnit, type HydratedInventoryLine } from "@forgepoint/shared";
import { formatAbsoluteTimestamp } from "@/lib/relativeTime";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { useInventoryStore } from "./inventoryStore";

const columns: DataTableColumn<HydratedInventoryLine>[] = [
  {
    key: "locationName",
    header: "Location",
    className: "min-w-28 align-top",
    format: (_, row) => row.locationName ?? "Unavailable location",
  },
  {
    key: "availableQty",
    header: "Available",
    align: "right",
    className: "whitespace-nowrap tabular-nums align-top",
    format: (_, row) =>
      row.availableQty === null
        ? "Unknown"
        : `${row.availableQty} ${formatItemUnit(row.quantityUnit)}`,
  },
  {
    key: "unitCost",
    header: "Unit cost",
    align: "right",
    className: "whitespace-nowrap tabular-nums align-top",
    format: (_, row) =>
      row.unitCost === null || row.costUnit === null
        ? "Unknown"
        : `${row.unitCost} ${row.currency} / ${formatItemUnit(row.costUnit)}`,
  },
  {
    key: "metadata",
    header: "Attributes",
    className: "min-w-40 align-top",
    format: (_, row) => <InventoryMetadata values={row.metadata} />,
  },
  {
    key: "updatedAt",
    header: "Last updated",
    className: "min-w-28 align-top",
    format: (_, row) => (
      <span className="text-xs text-muted-foreground">
        {formatAbsoluteTimestamp(row.updatedAt)}
      </span>
    ),
  },
];

/** Displays imported inventory for one item, without edit controls. */
export default function InventoryController({ itemId }: { itemId: string }) {
  const lines = useInventoryStore((state) => state.lines);
  const fetchLines = useInventoryStore((state) => state.fetchLines);
  const loading = useInventoryStore((state) => state.loadingByItemId[itemId]);
  const listLoading = loading?.isLoading ?? false;
  const listError = loading?.error;
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState<{ ids: string[]; total: number }>({
    ids: [],
    total: 0,
  });
  const pageSize = 50;

  useEffect(() => {
    let cancelled = false;
    fetchLines({ itemId, limit: pageSize, offset: page * pageSize }).then(
      (data) => {
        if (cancelled) {
          return;
        }
        const lastPage = Math.max(0, Math.ceil(data.total / pageSize) - 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setResult(data);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [fetchLines, itemId, page, refresh]);
  const entitiesArray = result.ids
    .map((id) => lines.get(id))
    .filter((line): line is HydratedInventoryLine => line !== undefined);
  const pagination =
    result.total > 0
      ? { page, pageSize, total: result.total, onPageChange: setPage }
      : undefined;
  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3">
      <p className="text-sm text-muted-foreground">
        Inventory is read-only. Quantities and costs reflect imported stock.
      </p>
      {listError && (
        <p role="alert" className="text-sm text-destructive">
          {listError}
        </p>
      )}
      <DataTable
        className="min-h-0 flex-1 [&>.overflow-auto]:scrollbar-transparent"
        columns={columns}
        data={entitiesArray}
        isLoading={listLoading}
        hideSearch
        toolbarActions={
          <Button
            variant="outline"
            disabled={listLoading}
            onClick={() => setRefresh((value) => value + 1)}
          >
            Refresh
          </Button>
        }
        pagination={pagination}
        emptyState={
          <div className="py-8 text-center text-sm text-muted-foreground">
            No matching inventory.
          </div>
        }
      />
    </div>
  );
}
